import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { ContentCoverVideo } from "~/components/content/content-cover-video";
import { isArticleVideoAsHero } from "~/components/content/content-cover-video/helpers";
import type { Props as ContentSchemaRenderProps } from "~/components/content/content-schema-render";
import { useContentSchemaRenderIframe } from "~/components/content/content-schema-render/iframe/hooks";
import type { articleLeadingArticle$key } from "~/queries/__generated__/articleLeadingArticle.graphql";

import type { Props as ArticleLeadingImagesProps } from "./article-leading-images";
import { ArticleLeadingImages } from "./article-leading-images";
import { ArticleLeadingVideoContainer } from "./styles";

export type Props = {
  className?: string;
  reference: articleLeadingArticle$key;
} & Omit<ArticleLeadingImagesProps, "reference" | "imageProps"> &
  Pick<ContentSchemaRenderProps, "advertising">;

export const ArticleLeading: FunctionComponent<Props> = ({
  advertising,
  className,
  reference: reference_,
  ...articleLeadingProps
}) => {
  const article = useFragment(
    graphql`
      fragment articleLeadingArticle on Article {
        displaySlideShow
        ...articleLeadingImagesContent
        ...contentCoverVideoContent
        ...contentSchemaRenderContent
        ...helpersIsArticleVideoAsHeroArticle
      }
    `,
    reference_,
  );

  const { displaySlideShow } = article;

  const { ComponentWithExtraProps: IframeWithExtraProps } = useContentSchemaRenderIframe({
    gaActionPrefix: "Youtube article header/",
  });

  if (isArticleVideoAsHero(article))
    return (
      <ArticleLeadingVideoContainer className={className}>
        <ContentCoverVideo
          advertising={advertising}
          extraComponentMap={{
            iframe: IframeWithExtraProps,
          }}
          reference={article}
        />
      </ArticleLeadingVideoContainer>
    );

  if (!displaySlideShow) return null;

  return (
    <ArticleLeadingImages
      {...articleLeadingProps}
      className={className}
      imageProps={{ lazyload: false }}
      reference={article}
    />
  );
};

ArticleLeading.displayName = "ArticleLeading";
